import { monthlyChargesMsg, singleFaresMsg, sumPerMonthMsg, t } from '../../common/i18n';
import { eurosToCents, formatSumToString } from '../../common/utils/priceUtils.js';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory.js';
import { PriceType } from '../HorizontalSubscriptionCard/types';
import { PlanProductRuntimeCharacteristic } from '../PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import {
  buildCharacteristicSelection,
  getCharacteristicValueLabel,
  getSelectedCharacteristicValue,
} from '../PlaceOrder/utils/runtimeCharacteristicsCoCo.js';
import type { PriceData } from './types';
import type { CLShoppingCartAddon, CLShoppingCartPrice } from '../../common/types/componentLibrary';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../common/types/shoppingBasketCoCo.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';
import type { CartProductOfferingCoco } from '../../generated/api/cartProductOfferingCoco.js';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ShoppingCartProps } from '@design-system/component-library';

type ChargeType = typeof ProductOfferingPrice.PriceTypeEnum.RC | typeof ProductOfferingPrice.PriceTypeEnum.OC;

const compareStrings = (left: string, right: string) => left.localeCompare(right);

const isMonthlyInstallmentPrice = (price: ProductOfferingPrice): boolean =>
  (price.installmentOption?.length === 1 && (price.installmentOption[0].numberOfInstallment ?? 0) > 1) ||
  (price.installmentTerm?.length === 1 && (price.installmentTerm[0].duration ?? 0) > 1);

// Catalog OC prices retain their type in the order payload, but a selected monthly installment
// belongs to the recurring charges in the basket and order summary.
const getChargeType = (price: ProductOfferingPrice) =>
  isMonthlyInstallmentPrice(price) ? PriceType.RECURRING_CHARGE : price.priceType;

export const getPrice = (prices: ProductOfferingPrice[], type: ChargeType): number =>
  prices.filter(p => getChargeType(p) === type).reduce((sum, p) => sum + (p.priceWithoutTax ?? 0), 0);

export const getEffectivePriceForItem = (basketItem: ProductOfferingCartItems, type: ChargeType): number => {
  const planPrice = basketItem.planProductOffering?.productOfferingPrice ?? [];
  return getPrice(planPrice, type) || getPrice(basketItem.productOfferingPrice ?? [], type);
};

export const getEffectivePrices = (basketItem: ProductOfferingCartItems): ProductOfferingPrice[] => {
  const planPrices = basketItem.planProductOffering?.productOfferingPrice ?? [];
  if (planPrices.length === 0) {
    return basketItem.productOfferingPrice ?? [];
  }

  const chargeTypes: ChargeType[] = [PriceType.RECURRING_CHARGE, PriceType.ONE_TIME_CHARGE];
  return chargeTypes.flatMap(type => {
    const planItems = planPrices.filter(p => getChargeType(p) === type);
    return planItems.length > 0
      ? planItems
      : (basketItem.productOfferingPrice ?? []).filter(p => getChargeType(p) === type);
  });
};

const getSelectedAddons = (product: ProductOfferingCartItems): CartProductOfferingCoco[] => {
  const selected = [
    ...(product.bundledAddonProductOffering ?? []).filter(addon => addon.isChecked),
    ...(product.commercialAddonProductOffering ?? []).filter(addon => addon.isChecked),
    ...(product.bundleAddonGroup ?? []).flatMap(group => (group.productOffering ?? []).filter(o => o.isSelected)),
  ];
  // The same offering can appear both in a bundle add-on group and in the bundled add-ons.
  return [...new Map(selected.map(addon => [addon.id, addon])).values()];
};

const isChargeable = (offering: CartProductOfferingCoco): boolean =>
  (offering.productOfferingPrice ?? []).some(price => (price.priceWithoutTax ?? 0) > 0);

/**
 * Add-ons that deserve their own row in the order summary, i.e. the ones the customer actually picked.
 * Bundle add-on group selections (SIM type, roaming area, mobile certificate, ...) are provisioning
 * configuration rather than separate order rows, so they are only listed when they cost something -
 * otherwise the summary rows would no longer add up to the total.
 */
export const getAddonsForOrderSummary = (product: ProductOfferingCartItems): CartProductOfferingCoco[] => {
  const groupIds = new Set(
    (product.bundleAddonGroup ?? []).flatMap(group => (group.productOffering ?? []).map(o => o.id))
  );
  return getSelectedAddons(product).filter(addon => !groupIds.has(addon.id) || isChargeable(addon));
};

export const calculateFee = (productOfferings: ProductOfferingCartItems[], type: ChargeType): number =>
  productOfferings.reduce((cartSum, product) => {
    const addonsSum = getSelectedAddons(product).reduce(
      (acc, addon) => acc + getPrice(addon.productOfferingPrice ?? [], type),
      0
    );
    const sum = getEffectivePriceForItem(product, type) + addonsSum;
    return cartSum + eurosToCents(sum);
  }, 0);

export const getProductPriceToDisplay = (price: ProductOfferingPrice[], quantity: number): CLShoppingCartPrice => {
  const recurringPrice = price.find(p => p.priceType === PriceType.RECURRING_CHARGE);
  const oneTimePrice = price.find(p => p.priceType === PriceType.ONE_TIME_CHARGE);
  const priceData = recurringPrice ?? oneTimePrice;

  // A device paid in monthly installments can be modelled either as a RECURRING_CHARGE or as a
  // ONE_TIME_CHARGE that carries a multi-installment option/term whose priceWithoutTax is the
  // monthly amount (see buildSelectedDevicePrice). In both cases the price is monthly (€/kk).
  const isMonthlyInstallment = price.some(isMonthlyInstallmentPrice);

  return {
    amount: formatSumToString(eurosToCents(priceData?.priceWithoutTax ?? 0) * quantity, true),
    unit: recurringPrice || isMonthlyInstallment ? t.YO7F(sumPerMonthMsg, '€') : '€',
  };
};

export const findBasketItemById = (basketItems: ProductOfferingCartItems[] | undefined, basketItemId: string) =>
  basketItems?.find(item => item.id === basketItemId);

export const findBasketItemByCartItemId = (basketItems: ProductOfferingCartItems[] | undefined, cartItemId: string) =>
  basketItems?.find(item => item.cartItemId === cartItemId);

/**
 * Builds a stable key representing the selected addon and payment configuration of a cart item.
 * Items with identical productId + checkedAddonKey are visually grouped in the UI.
 * Covers all three addon surfaces: bundledAddonProductOffering (isChecked),
 * commercialAddonProductOffering (isChecked), and bundleAddonGroup productOffering (isSelected).
 */
export const getCheckedAddonKey = (item: ProductOfferingCartItems): string => {
  const bundledIds = (item.bundledAddonProductOffering ?? [])
    .filter(a => a.isChecked)
    .map(a => a.id)
    .sort(compareStrings)
    .join(',');
  const commercialIds = (item.commercialAddonProductOffering ?? [])
    .filter(a => a.isChecked)
    .map(a => a.id)
    .sort(compareStrings)
    .join(',');
  const bundleGroupIds = (item.bundleAddonGroup ?? [])
    .flatMap(group => (group.productOffering ?? []).filter(o => o.isSelected).map(o => o.id))
    .sort(compareStrings)
    .join(',');
  const runtimeCharacteristics = (item.productOfferingRuntimeCharacteristic ?? [])
    .map(characteristic => `${characteristic.name}=${(characteristic.value ?? []).map(v => v.value).join('+')}`)
    .sort(compareStrings)
    .join(',');
  const paymentPrices = (item.productOfferingPrice ?? []).filter(
    price => price.installmentOption?.length || price.installmentTerm?.length
  );
  const paymentKey = paymentPrices.length > 0 ? `|${JSON.stringify(paymentPrices)}` : '';
  return `${bundledIds}|${commercialIds}|${bundleGroupIds}|${runtimeCharacteristics}${paymentKey}`;
};

/**
 * The fiber type is a configuration choice rather than a purchasable add-on, so it is listed in the
 * order summary without a price. The label is resolved from the catalog options kept on the plan.
 */
const getRuntimeCharacteristicDisplayData = (basketItem: ProductOfferingCartItems): CLShoppingCartAddon[] => {
  const selectedValue = getSelectedCharacteristicValue(
    basketItem.productOfferingRuntimeCharacteristic,
    PlanProductRuntimeCharacteristic.FIBER_MODE
  );
  if (!selectedValue) {
    return [];
  }
  const label = getCharacteristicValueLabel(
    basketItem.planProductOffering?.productOfferingRuntimeCharacteristic,
    PlanProductRuntimeCharacteristic.FIBER_MODE,
    selectedValue
  );
  return [
    {
      id: `runtime-characteristic-${selectedValue}`,
      name: `${t.RZQ4('Fiber type')}: ${label}`,
      price: getProductPriceToDisplay([], 1),
      quantity: 1,
    },
  ];
};

export const getAddonsDisplayData = (basketItem: ProductOfferingCartItems): CLShoppingCartAddon[] => [
  ...getRuntimeCharacteristicDisplayData(basketItem),
  ...getSelectedAddons(basketItem).map(addon => ({
    id: addon.id,
    name: addon.name.value ?? '',
    price: getProductPriceToDisplay(addon.productOfferingPrice ?? [], 1),
    quantity: 1,
  })),
];

export const calculateChargeTotals = (items: ProductOfferingCartItems[] | undefined) =>
  items
    ? {
        monthlyRecurringCharges: calculateFee(items, PriceType.RECURRING_CHARGE),
        onetimeCharges: calculateFee(items, PriceType.ONE_TIME_CHARGE),
      }
    : { monthlyRecurringCharges: 0, onetimeCharges: 0 };

export const formatTotalPrices = (items: ProductOfferingCartItems[] | undefined): ShoppingCartProps['totals'] => {
  const { monthlyRecurringCharges, onetimeCharges } = calculateChargeTotals(items);

  return [
    ...(monthlyRecurringCharges
      ? [
          {
            name: t.P6BC(monthlyChargesMsg),
            amount: formatSumToString(monthlyRecurringCharges, true),
            unit: t.H37G('€/month'),
          },
        ]
      : []),
    ...(onetimeCharges
      ? [
          {
            name: t.GOBY(singleFaresMsg),
            amount: formatSumToString(onetimeCharges, true),
            unit: '€',
          },
        ]
      : []),
  ];
};

/**
 * Updates only the basket entries whose cartItemId is in the given set.
 * Used when updating addons for a specific group of entries (same product + same addon selection).
 */
function updateProductOfferingsByCartItemIds(
  shoppingBasket: ShoppingBasketType,
  cartItemIds: string[],
  updater: (item: ProductOfferingCartItems) => ProductOfferingCartItems
): ShoppingBasketType {
  if (!shoppingBasket.productOfferings) {
    return { ...shoppingBasket };
  }
  const idsToUpdate = new Set(cartItemIds);
  const newProductOfferings = shoppingBasket.productOfferings.map(item =>
    idsToUpdate.has(item.cartItemId) ? updater(item) : item
  );
  return { ...shoppingBasket, productOfferings: newProductOfferings };
}

export const updateBasketItemQuantity = (
  shoppingBasket: ShoppingBasketType,
  cartItemId: string,
  quantity: number
): ShoppingBasketType => {
  if (quantity !== 0) {
    return shoppingBasket;
  }
  return {
    ...shoppingBasket,
    productOfferings: (shoppingBasket.productOfferings ?? []).filter(item => item.cartItemId !== cartItemId),
  };
};

export const addEntriesToBasket = (
  basket: ShoppingBasketType,
  template: ProductOfferingCartItems,
  count: number
): ShoppingBasketType => ({
  ...basket,
  productOfferings: [
    ...(basket.productOfferings ?? []),
    ...Array.from({ length: count }, () => ({
      ...template,
      cartItemId: `cartItem-${crypto.randomUUID()}`,
    })),
  ],
});

export const removeEntriesByCartItemIds = (basket: ShoppingBasketType, cartItemIds: string[]): ShoppingBasketType => {
  const toRemove = new Set(cartItemIds);
  return {
    ...basket,
    productOfferings: (basket.productOfferings ?? []).filter(item => !toRemove.has(item.cartItemId)),
  };
};

export const applyQuantityChangeToBasket = (
  basket: ShoppingBasketType,
  newQty: number,
  groupItems: ProductOfferingCartItems[]
): ShoppingBasketType => {
  const itemCountChange = newQty - groupItems.length;
  if (itemCountChange === 0) {
    return basket;
  }
  if (newQty === 0 || itemCountChange < 0) {
    const removeCount = newQty === 0 ? groupItems.length : -itemCountChange;
    const toRemoveIds = groupItems.slice(-removeCount).map(item => item.cartItemId);
    return removeEntriesByCartItemIds(basket, toRemoveIds);
  }
  return addEntriesToBasket(basket, groupItems[0], itemCountChange);
};

export const updateAddonInBasket = (
  shoppingBasket: ShoppingBasketType,
  addonId: string,
  groupCartItemIds: string[]
): ShoppingBasketType =>
  updateProductOfferingsByCartItemIds(shoppingBasket, groupCartItemIds, item => ({
    ...item,
    bundledAddonProductOffering: item.bundledAddonProductOffering
      ? item.bundledAddonProductOffering.map(addOn =>
          addOn.id === addonId ? { ...addOn, isChecked: !addOn.isChecked } : addOn
        )
      : undefined,
    commercialAddonProductOffering: item.commercialAddonProductOffering
      ? item.commercialAddonProductOffering.map(addOn =>
          addOn.id === addonId ? { ...addOn, isChecked: !addOn.isChecked } : addOn
        )
      : undefined,
  }));

export const updateAddonGroupSelectionInBasket = (
  shoppingBasket: ShoppingBasketType,
  groupCartItemIds: string[],
  groupId: string,
  selectedOfferingIds: string[]
): ShoppingBasketType =>
  updateProductOfferingsByCartItemIds(shoppingBasket, groupCartItemIds, item => {
    const groupOfferingIds = new Set(
      (item.bundleAddonGroup ?? []).find(g => g.id === groupId)?.productOffering?.map(o => o.id) ?? []
    );
    return {
      ...item,
      bundleAddonGroup: (item.bundleAddonGroup ?? []).map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            productOffering: (g.productOffering ?? []).map(o => ({
              ...o,
              isSelected: selectedOfferingIds.includes(o.id),
            })),
          };
        }

        return g;
      }),
      bundledAddonProductOffering: item.bundledAddonProductOffering?.map(addOn =>
        groupOfferingIds.has(addOn.id) ? { ...addOn, isChecked: selectedOfferingIds.includes(addOn.id) } : addOn
      ),
    };
  });

export const updateProductOfferingRuntimeCharacteristicInBasket = (
  shoppingBasket: ShoppingBasketType,
  groupCartItemIds: string[],
  selectedValue: string
): ShoppingBasketType =>
  updateProductOfferingsByCartItemIds(shoppingBasket, groupCartItemIds, item => {
    const selection = buildCharacteristicSelection(
      item.planProductOffering?.productOfferingRuntimeCharacteristic,
      PlanProductRuntimeCharacteristic.FIBER_MODE,
      selectedValue
    );
    return selection ? { ...item, productOfferingRuntimeCharacteristic: selection } : item;
  });

const toPriceData = (monthlyRecurringCharge: number, oneTimeCharge: number): PriceData => ({
  price: {
    ...(monthlyRecurringCharge ? { monthlyRecurringCharge } : {}),
    ...(oneTimeCharge ? { oneTimeCharge } : {}),
  },
});

/**
 * Price of the basket item itself, in cents. Selected add-ons are deliberately excluded: the order
 * summary lists every selected add-on on its own row with its own price.
 */
export const getPriceDataForBasketItem = (cartItem: ProductOfferingCartItems): PriceData => {
  const priceData = toPriceData(
    eurosToCents(getEffectivePriceForItem(cartItem, PriceType.RECURRING_CHARGE)),
    eurosToCents(getEffectivePriceForItem(cartItem, PriceType.ONE_TIME_CHARGE))
  );
  const installmentPrice = getEffectivePrices(cartItem).find(isMonthlyInstallmentPrice);
  if (installmentPrice && priceData.price) {
    priceData.price.payments =
      installmentPrice.installmentOption?.[0]?.numberOfInstallment ?? installmentPrice.installmentTerm?.[0]?.duration;
  }
  return priceData;
};

/** Price of a single add-on selected for a basket item, in cents. A free add-on keeps a zero price so
 * that the order summary shows "0,00 €" for it instead of leaving the price cell empty. */
export const getPriceDataForAddon = (addon: CartProductOfferingCoco): PriceData => {
  const prices = addon.productOfferingPrice ?? [];
  const hasRecurring = prices.some(p => p.priceType === PriceType.RECURRING_CHARGE);
  const hasOneTime = prices.some(p => p.priceType === PriceType.ONE_TIME_CHARGE);

  return {
    price: {
      ...(hasRecurring ? { monthlyRecurringCharge: eurosToCents(getPrice(prices, PriceType.RECURRING_CHARGE)) } : {}),
      ...(hasOneTime || !hasRecurring
        ? { oneTimeCharge: eurosToCents(getPrice(prices, PriceType.ONE_TIME_CHARGE)) }
        : {}),
    },
  };
};

export const isFixedLineEquipmentGroup = (addonGroup: CartBundleAddonGroup): boolean =>
  addonGroup.productOffering?.some(offering => offering.productOfferingType?.category === OnlineModelCategory.CPE) ===
  true;

export const getSelectedOfferingIds = (
  addonGroup: CartBundleAddonGroup,
  basketItem: ProductOfferingCartItems
): string[] =>
  (addonGroup.productOffering ?? [])
    .filter(
      offering =>
        offering.isSelected ||
        basketItem.bundledAddonProductOffering?.some(addon => addon.id === offering.id && addon.isChecked)
    )
    .map(offering => offering.id);

export const updatePlanProductOfferingSpecRuntimeCharacteristicInBasket = (
  shoppingBasket: ShoppingBasketType,
  cartItemIds: string[],
  selectedValue: string,
  characteristicName: PlanProductRuntimeCharacteristic
): ShoppingBasketType =>
  updateProductOfferingsByCartItemIds(shoppingBasket, cartItemIds, item => {
    if (!item.planProductOffering) {
      return item;
    }

    const characteristics = item.planProductOffering.productSpecRuntimeCharacteristics ?? [];
    const hasCharacteristic = characteristics.some(c => c.name === characteristicName);

    const updatedCharacteristics = hasCharacteristic
      ? characteristics.map(c => (c.name === characteristicName ? { ...c, value: [{ value: selectedValue }] } : c))
      : [
          ...characteristics,
          {
            name: characteristicName,
            type: 'String',
            value: [{ value: selectedValue }],
          },
        ];

    return {
      ...item,
      planProductOffering: {
        ...item.planProductOffering,
        productSpecRuntimeCharacteristics: updatedCharacteristics,
      },
    };
  });
